import React, { useMemo, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatFeesDisplay } from './optionsOpenPositionsUtils';
import { formatCompactPnl, formatDateTime, formatHistoryRollLabel, formatHistoryStatus } from './optionsHistoryUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsCampaignHistorySection } from './OptionsCampaignHistorySection';
import { OptionsMonetaryText } from './OptionsMonetaryText';
import { OptionsUnderlyingStrategyTitle } from './OptionsUnderlyingStrategyTitle';
import GeniLoader from '../../components/GeniLoader';

export const OptionsHistoryCampaignCard = ({ campaign, detail, index, selectedCurrency, onExpand, loadingDetail }) => {
  const { t, isPortuguese } = useOptionsLocale();
  const [expanded, setExpanded] = useState(false);
  const pnl = formatCompactPnl({
    selectedCurrency,
    nativeValue: campaign.campaign_net_pnl_base,
    usdValue: campaign.campaign_net_pnl_usd,
    currency: campaign.underlying,
  });
  const fees = formatFeesDisplay({ selectedCurrency, nativeValue: campaign.total_fees_base, usdValue: campaign.total_fees_usd, underlying: campaign.underlying });

  const orderedOperations = useMemo(() => {
    return [...(detail?.operations || [])].sort((a, b) => new Date(a.opened_at || 0).getTime() - new Date(b.opened_at || 0).getTime());
  }, [detail]);

  const rollTypeBetween = (sourceOperationId, targetOperationId) => {
    const types = (detail?.roll_links || [])
      .filter((link) => link.source_operation_id === sourceOperationId && link.target_operation_id === targetOperationId)
      .flatMap((link) => link.roll_types || []);
    return Array.from(new Set(types));
  };

  const handleToggle = async (value) => {
    const isOpen = Boolean(value);
    setExpanded(isOpen);
    if (isOpen && !detail) {
      await onExpand(campaign.id);
    }
  };

  return (
    <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={`options-history-campaign-card-${index}`}>
      <CardContent className="p-5 sm:p-6">
        <Accordion type="single" collapsible value={expanded ? campaign.id : undefined} onValueChange={handleToggle}>
          <AccordionItem value={campaign.id} className="border-none">
            <div className="space-y-6">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2" data-testid={`options-history-campaign-badges-${index}`}>
                    <Badge className="border-white/10 bg-white/[0.05] text-white">{formatHistoryStatus(campaign.status, t)}</Badge>
                    {campaign.roll_link_count > 0 ? <Badge className="border-[#00E676]/20 bg-[#00E676]/10 text-[#9BFFBE]">{formatHistoryRollLabel(t)}</Badge> : null}
                  </div>
                  <div className="space-y-2">
                    <OptionsUnderlyingStrategyTitle
                      as="h2"
                      underlying={campaign.underlying}
                      strategy={campaign.strategy_origin}
                      iconSize={18}
                      className="text-2xl font-black font-heading tracking-tight text-white sm:text-3xl"
                      testId={`options-history-campaign-strategy-${index}`}
                    />
                    <div className="flex flex-wrap items-center gap-2 text-sm text-white/60" data-testid={`options-history-campaign-meta-${index}`}>
                      <span>{campaign.underlying}</span>
                      <span>·</span>
                      <span>{t('history.campaignsOperationCount').replace('{count}', campaign.operation_count)}</span>
                      <span>·</span>
                      <span>{t('history.campaignsRollCount').replace('{count}', campaign.roll_link_count)}</span>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 xl:w-[460px] xl:grid-cols-2" data-testid={`options-history-campaign-summary-${index}`}>
                  <Metric label={t('history.opened')} value={formatDateTime(campaign.opened_at, isPortuguese ? 'pt' : 'en')} />
                  <Metric label={t('common.status')} value={formatHistoryStatus(campaign.status, t)} />
                  <Metric label={t('history.campaignNetPnl')} value={pnl.primary} secondary={pnl.secondary} />
                  <Metric label={t('dashboard.fees')} value={fees.primary} secondary={fees.secondary} />
                </div>
              </div>

              <AccordionTrigger className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-4 text-left text-sm font-semibold text-white no-underline hover:no-underline" data-testid={`options-history-campaign-expand-${index}`}>
                <div className="flex flex-col gap-1">
                  <span>{expanded ? t('history.hideTimeline') : t('history.viewTimeline')}</span>
                  <span className="text-xs font-normal text-white/45">{t('history.timelineHelper')}</span>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pt-2" data-testid={`options-history-campaign-details-${index}`}>
                {loadingDetail && !detail ? (
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-8" data-testid={`options-history-campaign-loading-${index}`}>
                    <div className="min-h-[120px]">
                      <GeniLoader size="md" />
                    </div>
                  </div>
                ) : detail ? (
                  <div className="space-y-5 rounded-[28px] border border-white/10 bg-black/20 p-5 sm:p-6" data-testid={`options-history-campaign-timeline-${index}`}>
                    <OptionsCampaignHistorySection
                      detail={detail}
                      selectedCurrency={selectedCurrency}
                      testIdPrefix={`options-history-campaign-history-${index}`}
                    />
                  </div>
                ) : null}
              </AccordionContent>
            </div>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

const Metric = ({ label, value, secondary = null }) => (
  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4">
    <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
    <p className="mt-2 text-sm font-semibold text-white tabular-nums"><OptionsMonetaryText value={value} iconSize={15} /></p>
    {secondary ? <p className="mt-1 text-xs text-white/50 tabular-nums"><OptionsMonetaryText value={secondary} iconSize={13} /></p> : null}
  </div>
);

export default OptionsHistoryCampaignCard;